import { useEffect, useId, useState } from 'react';
import { eventBus, getLocalDataEventName, getLocalDataSnapshot } from '../foundation';

/**
 * Reads a value persisted by the `save_local_data:<key>` player action and keeps it live.
 *
 * Returns the stored value synchronously on the very first render (localStorage-backed) and
 * re-renders whenever {@link saveLocalData} broadcasts a new value for `key` via the eventBus.
 *
 * @param key storage key, e.g. `theme` or `lang`
 * @returns the current value, or `null` if absent
 */
export const useLocalData = <T>(key: string): T | null => {
    const [value, setValue] = useState<T | null>(() => getLocalDataSnapshot<T>(key));
    const subscriptionId = useId();

    useEffect(() => {
        // Initial value comes from the synchronous useState initializer above; the eventBus
        // ReplaySubject buffer replays any write that landed in the render→effect gap.
        const eventName = getLocalDataEventName(key);
        eventBus.subscribe(eventName, subscriptionId, (next) => setValue((next as T) ?? null));

        return () => {
            eventBus.unsubscribe(eventName, subscriptionId);
        };
    }, [key, subscriptionId]);

    return value;
};
